import React, { useState, Component } from 'react';
import { Plus, X, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { sources as initialSources } from '../lib/data';
function getTierColor(tierId: number | string) {
  switch (tierId.toString()) {
    case '1':
      return 'bg-tier-1 text-white';
    case '2':
      return 'bg-tier-2 text-white';
    case '3':
      return 'bg-tier-3 text-white';
    case 'specialized':
      return 'bg-tier-special text-white';
    default:
      return 'bg-muted text-foreground';
  }
}
type LocalSource = {
  id: string;
  name: string;
  tier: string | number | null;
  logo: string;
};
type LocalTier = {
  id: string | number;
  name: string;
  isCustom: boolean;
};
export function TierManagement() {
  const [sources, setSources] = useState<LocalSource[]>(initialSources.map(s => ({
    id: s.name,
    name: s.name,
    tier: s.tier,
    logo: s.logo
  })));
  const [tiers, setTiers] = useState<LocalTier[]>([{
    id: 1,
    name: 'Tier 1',
    isCustom: false
  }, {
    id: 2,
    name: 'Tier 2',
    isCustom: false
  }, {
    id: 3,
    name: 'Tier 3',
    isCustom: false
  }, {
    id: 'specialized',
    name: 'Specializzata',
    isCustom: false
  }]);
  const [isAddSourceOpen, setIsAddSourceOpen] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [isAddTierOpen, setIsAddTierOpen] = useState(false);
  const [newTierName, setNewTierName] = useState('');
  const [filter, setFilter] = useState<string | number | 'all' | 'unassigned'>('all');
  const unassignedSources = sources.filter(s => s.tier === null);
  const handleAddSource = () => {
    if (!newSourceName.trim()) return;
    const newSource: LocalSource = {
      id: newSourceName,
      name: newSourceName,
      tier: null,
      logo: `https://ui-avatars.com/api/?name=${encodeURIComponent(newSourceName)}&background=random&color=fff&size=32&font-size=0.4`
    };
    setSources([...sources, newSource]);
    setNewSourceName('');
    setIsAddSourceOpen(false);
  };
  const handleAddTier = () => {
    if (!newTierName.trim()) return;
    const newTierId = newTierName.toLowerCase().replace(/\s+/g, '-');
    if (tiers.some(t => t.id === newTierId)) return; // prevent duplicates
    const newTier: LocalTier = {
      id: newTierId,
      name: newTierName,
      isCustom: true
    };
    setTiers([...tiers, newTier]);
    setNewTierName('');
    setIsAddTierOpen(false);
  };
  const handleDeleteTier = (tierId: string | number) => {
    // Unassign all sources in this tier
    setSources(sources.map(s => s.tier === tierId ? {
      ...s,
      tier: null
    } : s));
    // Remove tier
    setTiers(tiers.filter(t => t.id !== tierId));
  };
  const handleReassignSource = (sourceId: string, newTierId: string | number | null) => {
    // Convert string numbers back to numbers if needed for default tiers
    let parsedTierId = newTierId;
    if (newTierId === '1' || newTierId === '2' || newTierId === '3') {
      parsedTierId = parseInt(newTierId, 10);
    }
    setSources(sources.map(s => s.id === sourceId ? {
      ...s,
      tier: parsedTierId
    } : s));
  };
  const filteredSources = sources.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'unassigned') return s.tier === null;
    return s.tier === filter;
  });
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full space-y-8" data-id="element-1624">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1625">
        <div data-id="element-1626">
          <h1 className="text-2xl md:text-3xl font-normal tracking-tight" data-id="element-1627">
            Gestione Tier
          </h1>
        </div>
        <div className="flex items-center gap-3" data-id="element-1628">
          <Button variant="outline" className="rounded-full h-11 px-6 text-sm" onClick={() => setIsAddTierOpen(true)} data-id="element-1629">
            <Plus className="h-4 w-4 mr-1.5" data-id="element-1630" />
            Nuovo Tier
          </Button>
          <Button className="rounded-full h-11 px-6 text-sm" onClick={() => setIsAddSourceOpen(true)} data-id="element-1631">
            <Plus className="h-4 w-4 mr-1.5" data-id="element-1632" />
            Aggiungi testata
          </Button>
        </div>
      </header>

      <div className="bg-card border border-border rounded-lg overflow-hidden" data-id="element-1633">
        {/* Filter tabs */}
        <div className="flex items-center gap-6 px-6 pt-5 pb-0 text-sm overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-1634">
          <button onClick={() => setFilter('all')} className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${filter === 'all' ? 'border-accent text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-id="element-1635">
            Tutte{' '}
            <span className="text-muted-foreground ml-1" data-id="element-1636">
              ({sources.length})
            </span>
          </button>
          {tiers.map(t => {
          const count = sources.filter(s => s.tier === t.id).length;
          return <button key={t.id} onClick={() => setFilter(t.id)} className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${filter === t.id ? 'border-accent text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-id="element-1637">
                {t.name}{' '}
                <span className="text-muted-foreground ml-1" data-id="element-1638">({count})</span>
              </button>;
        })}
          <button onClick={() => setFilter('unassigned')} className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${filter === 'unassigned' ? 'border-accent text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-id="element-1639">
            Non assegnate{' '}
            <span className="text-muted-foreground ml-1" data-id="element-1640">
              ({unassignedSources.length})
            </span>
          </button>
        </div>

        <div className="border-t border-border" data-id="element-1641" />

        {/* Table */}
        <Table data-id="element-1642">
          <TableHeader data-id="element-1643">
            <TableRow className="hover:bg-transparent" data-id="element-1644">
              <TableHead data-id="element-1645">Testata</TableHead>
              <TableHead className="w-[200px]" data-id="element-1646">Tier attuale</TableHead>
              <TableHead className="w-[250px] text-right" data-id="element-1647">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-1648">
            {filteredSources.length === 0 ? <TableRow className="hover:bg-transparent" data-id="element-1649">
                <TableCell colSpan={3} className="h-32 text-center text-muted-foreground" data-id="element-1650">
                  Nessuna testata trovata per questo filtro.
                </TableCell>
              </TableRow> : filteredSources.map(source => {
            const currentTier = tiers.find(t => t.id === source.tier);
            return <TableRow key={source.id} className="hover:bg-muted/30 transition-colors" data-id="element-1651">
                    <TableCell data-id="element-1652">
                      <div className="flex items-center gap-3" data-id="element-1653">
                        <img src={source.logo} alt={source.name} className="w-8 h-8 rounded-md flex-shrink-0" data-id="element-1654" />
                        <span className="font-medium" data-id="element-1655">{source.name}</span>
                      </div>
                    </TableCell>
                    <TableCell data-id="element-1656">
                      {currentTier ? <Badge className={`font-normal px-2.5 py-1 text-xs border-0 ${getTierColor(currentTier.id)}`} data-id="element-1657">
                          {currentTier.name}
                        </Badge> : <span className="text-sm text-muted-foreground" data-id="element-1658">
                          Non assegnato
                        </span>}
                    </TableCell>
                    <TableCell className="text-right" data-id="element-1659">
                      <div className="flex items-center justify-end gap-3" data-id="element-1660">
                        <select value={source.tier?.toString() || ''} onChange={e => handleReassignSource(source.id, e.target.value)} className="text-sm bg-transparent border border-border rounded-md px-2 py-1.5 outline-none focus:border-accent w-[140px]" data-id="element-1661">
                          <option value="" disabled data-id="element-1662">
                            Assegna a...
                          </option>
                          {tiers.map(t => <option key={t.id} value={t.id} data-id="element-1663">
                              {t.name}
                            </option>)}
                        </select>
                        <Button variant="ghost" size="icon-sm" onClick={() => handleReassignSource(source.id, null)} className="text-muted-foreground hover:text-destructive" title="Rimuovi dal tier" disabled={source.tier === null} data-id="element-1664">
                          <X className="h-4 w-4" data-id="element-1665" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>;
          })}
          </TableBody>
        </Table>
      </div>

      {/* Add Source Dialog */}
      <Dialog open={isAddSourceOpen} onOpenChange={setIsAddSourceOpen} data-id="element-1666">
        <DialogContent className="sm:max-w-[400px]" data-id="element-1667">
          <DialogHeader data-id="element-1668">
            <DialogTitle data-id="element-1669">Nuova testata</DialogTitle>
            <DialogDescription data-id="element-1670">
              Aggiungi una nuova testata all'elenco. Potrai poi assegnarla a un
              tier.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4" data-id="element-1671">
            <input type="text" placeholder="Nome testata (es. Il Sole 24 Ore)" value={newSourceName} onChange={e => setNewSourceName(e.target.value)} className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-accent" autoFocus data-id="element-1672" />
          </div>
          <div className="flex justify-end gap-2" data-id="element-1673">
            <Button variant="outline" onClick={() => setIsAddSourceOpen(false)} data-id="element-1674">
              Annulla
            </Button>
            <Button onClick={handleAddSource} disabled={!newSourceName.trim()} data-id="element-1675">
              Aggiungi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Tier Dialog */}
      <Dialog open={isAddTierOpen} onOpenChange={setIsAddTierOpen} data-id="element-1676">
        <DialogContent className="sm:max-w-[400px]" data-id="element-1677">
          <DialogHeader data-id="element-1678">
            <DialogTitle data-id="element-1679">Nuovo Tier</DialogTitle>
            <DialogDescription data-id="element-1680">
              Crea un nuovo raggruppamento per le testate.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4" data-id="element-1681">
            <input type="text" placeholder="Nome tier (es. Stampa Locale)" value={newTierName} onChange={e => setNewTierName(e.target.value)} className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-accent" autoFocus data-id="element-1682" />
          </div>
          <div className="flex justify-end gap-2" data-id="element-1683">
            <Button variant="outline" onClick={() => setIsAddTierOpen(false)} data-id="element-1684">
              Annulla
            </Button>
            <Button onClick={handleAddTier} disabled={!newTierName.trim()} data-id="element-1685">
              Crea Tier
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
}